import { createSelector } from 'reselect';

import { IRetailerCharactersSlice } from '@lib/core/characters/slices';
import { TParsedCharacter, parseCharacter } from '@lib/core/characters/utils/parseCharacter';
import { TProductCategory } from '@lib/core/products/types';
import { isProductCategory } from '@lib/tools/shared/helpers/interfaces';

/**
 * @returns the characters slice state
 */
export const selectCharactersState = (state): IRetailerCharactersSlice => state.characters;

/**
 * @returns all characters array
 */
export const selectCharacters = createSelector([selectCharactersState], charactersState =>
  charactersState.data.map(character => parseCharacter({ character })),
);

/**
 * @returns all characters as JSON with identifiers as keys
 */
export const selectCharactersByIdentifiers = createSelector([selectCharacters], characters => {
  const charactersByIdentifiers: Record<string, TParsedCharacter> = {};
  characters.forEach(character => {
    charactersByIdentifiers[character.characterId] = character;
  });
  return charactersByIdentifiers;
});

/**
 * @returns loading state for characters
 */
export const selectIsCharactersLoading = createSelector(
  [selectCharactersState],
  characters => characters.isLoading || false,
);
/**
 * @returns characters by product category
 */
export const selectCharactersByProductCategory = createSelector([selectCharacters], charactersArray => {
  return charactersArray.reduce(
    (acc, characterData) => {
      const {
        characterProductCategory: { name: pc },
      } = characterData;

      if (isProductCategory(pc)) {
        if (!acc[pc]) {
          acc[pc] = [];
        }
        acc[pc].push(characterData);
      }

      return acc;
    },
    {} as Record<TProductCategory, TParsedCharacter[]>,
  );
});

/**
 * @returns characters by character type
 */
export const selectCharactersByCharacterType = createSelector(
  [selectCharactersByProductCategory],
  (charactersByProductCategory): Record<TProductCategory, Record<string, TParsedCharacter[]>> => {
    const charactersByCharacterType: Record<TProductCategory, Record<string, TParsedCharacter[]>> = {} as Record<
      TProductCategory,
      Record<string, TParsedCharacter[]>
    >;

    Object.keys(charactersByProductCategory).forEach(productCategory => {
      const characterForThisProductCategory = charactersByProductCategory[productCategory];

      if (!charactersByCharacterType[productCategory]) {
        charactersByCharacterType[productCategory] = {};
      }

      characterForThisProductCategory.forEach(character => {
        const { characterType: { slug: characterTypeSlug = '' } = {} } = character;

        if (characterTypeSlug) {
          if (!charactersByCharacterType[productCategory][characterTypeSlug]) {
            charactersByCharacterType[productCategory][characterTypeSlug] = [];
          }

          charactersByCharacterType[productCategory][characterTypeSlug].push(character);
        }
      });
    });

    return charactersByCharacterType;
  },
);
